import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = {
  name: "", slug: "", description: "", image_url: "", display_order: 0, is_active: true,
  seo_title: "", seo_description: "", seo_keywords: "", og_image: "", seo_robots: "index,follow",
};

const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/categories").then(r => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/admin/categories/${editing.id}`, editing);
      else await api.post("/admin/categories", editing);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div><p className="eyebrow mb-1">Catalog</p><h1 className="font-hero text-3xl">Categories</h1></div>
        <button className="btn-primary" onClick={() => { setEditing({ ...empty }); setOpen(true); }}><Plus size={14} className="mr-2" /> New category</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.map(c => (
          <div key={c.id} className="bg-white rounded-2xl border border-border p-4 flex gap-4">
            <div className="w-20 h-24 rounded-xl bg-blush/40 overflow-hidden flex-shrink-0">{c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1">
              <p className="font-cormorant text-xl">{c.name}</p>
              <p className="text-xs text-charcoal/70">/shop/{c.slug}</p>
              <div className="flex gap-2 mt-3">
                <button className="text-charcoal" onClick={() => { setEditing(c); setOpen(true); }} aria-label="edit"><Pencil size={14} /></button>
                <button className="text-rose" onClick={() => remove(c)} aria-label="delete"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : setOpen(false))}>
        <DialogContent className="bg-cream max-w-lg">
          <DialogHeader><DialogTitle className="font-hero text-2xl">{editing?.id ? "Edit" : "New"} category</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[75vh] overflow-y-auto pr-1">
              <div><label className="eyebrow">Name</label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="cat-name" /></div>
              <div><label className="eyebrow">Slug</label><Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} data-testid="cat-slug" /></div>
              <div><label className="eyebrow">Short description (shown in CMS / hero)</label><Textarea rows={2} value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })} data-testid="cat-description" /></div>
              <div><label className="eyebrow">Image URL (cover)</label><Input value={editing.image_url || ""} onChange={e => setEditing({ ...editing, image_url: e.target.value })} data-testid="cat-image" /></div>
              <div><label className="eyebrow">Display order</label><Input type="number" value={editing.display_order || 0} onChange={e => setEditing({ ...editing, display_order: Number(e.target.value) })} data-testid="cat-order" /></div>

              {/* SEO panel */}
              <div className="rounded-xl border border-border bg-blush/20 p-4 mt-2 space-y-3" data-testid="cat-seo-panel">
                <div className="flex items-center gap-2">
                  <Search size={14} className="text-rose" />
                  <p className="eyebrow">Search engine listing (SEO)</p>
                </div>
                <p className="text-xs text-charcoal/70 -mt-2">
                  These fields control how this category appears in Google search and link previews on WhatsApp, LinkedIn, etc.
                </p>
                <div>
                  <label className="eyebrow">Meta title <span className="normal-case tracking-normal text-charcoal/60">— recommended 50–60 characters</span></label>
                  <Input
                    value={editing.seo_title || ""}
                    onChange={e => setEditing({ ...editing, seo_title: e.target.value })}
                    placeholder={`${editing.name || "Category"} — Rieave`}
                    data-testid="cat-seo-title"
                  />
                  <p className="text-xs text-charcoal/60 mt-1">{(editing.seo_title || "").length} / 60</p>
                </div>
                <div>
                  <label className="eyebrow">Meta description <span className="normal-case tracking-normal text-charcoal/60">— recommended 140–160 characters</span></label>
                  <Textarea
                    rows={3}
                    value={editing.seo_description || ""}
                    onChange={e => setEditing({ ...editing, seo_description: e.target.value })}
                    placeholder={`Discover handcrafted ${(editing.name || "").toLowerCase()} — perfect for every day.`}
                    data-testid="cat-seo-description"
                  />
                  <p className="text-xs text-charcoal/60 mt-1">{(editing.seo_description || "").length} / 160</p>
                </div>
                <div>
                  <label className="eyebrow">Keywords <span className="normal-case tracking-normal text-charcoal/60">(comma separated, optional)</span></label>
                  <Input
                    value={editing.seo_keywords || ""}
                    onChange={e => setEditing({ ...editing, seo_keywords: e.target.value })}
                    placeholder={`${(editing.name || "").toLowerCase()}, jewelry, gold, silver`}
                    data-testid="cat-seo-keywords"
                  />
                </div>
                <div>
                  <label className="eyebrow">Open Graph image URL <span className="normal-case tracking-normal text-charcoal/60">(used for social previews; falls back to cover image)</span></label>
                  <Input
                    value={editing.og_image || ""}
                    onChange={e => setEditing({ ...editing, og_image: e.target.value })}
                    placeholder="https://…"
                    data-testid="cat-og-image"
                  />
                </div>

                {/* Indexing toggle */}
                <div>
                  <label className="eyebrow">Search engine indexing</label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="cat-seo-robots">
                    {[
                      { v: "index,follow", l: "Index & Follow", hint: "Show in Google" },
                      { v: "noindex,nofollow", l: "Hide (noindex,nofollow)", hint: "Hide from search engines" },
                    ].map((opt) => {
                      const active = (editing.seo_robots || "index,follow") === opt.v;
                      return (
                        <button
                          type="button"
                          key={opt.v}
                          onClick={() => setEditing({ ...editing, seo_robots: opt.v })}
                          className={`text-xs px-3 py-2 rounded-full border transition ${active ? "border-rose bg-rose/10 text-rose" : "border-border text-charcoal/70 hover:border-rose/50"}`}
                          data-testid={`cat-robots-${opt.v.split(',')[0]}`}
                        >
                          <span className="font-medium">{opt.l}</span>
                          <span className="text-charcoal/50 ml-1.5">— {opt.hint}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Live SERP preview */}
                <div className="rounded-lg bg-white border border-border p-3" data-testid="cat-seo-preview">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal/50 mb-1">Google preview</p>
                  <p className="text-[#1a0dab] text-base font-body truncate">
                    {editing.seo_title || `${editing.name || "Category"} — Rieave`}
                  </p>
                  <p className="text-[#006621] text-xs truncate">
                    rieave.com/shop/{editing.slug || "your-slug"}
                  </p>
                  <p className="text-charcoal/70 text-xs line-clamp-2">
                    {editing.seo_description || `Discover handcrafted ${(editing.name || "").toLowerCase()} — perfect for every day.`}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button className="btn-outline" onClick={() => setOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={save} data-testid="cat-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategories;
